import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useInventoryStore } from '../../../stores/appInventoryStore';
import { CategoryNameExistsError, InventoryCategory } from '../../../services/inventory/inventoryService';
import { SortableList } from '../../common/SortableList';
import { InventorySlideOver, slideOverCancelClass, slideOverPrimaryClass } from '../../ui/InventorySlideOver';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  restaurantId: string;
}

interface RowProps {
  category: InventoryCategory;
  sectionCount: number;
  isEditing: boolean;
  editingName: string;
  onEditingNameChange: (v: string) => void;
  onStartEdit: () => void;
  onCommitEdit: () => void;
  onCancelEdit: () => void;
  onDelete: () => void;
  openMenuId: string | null;
  onOpenMenuChange: (id: string | null) => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
}

function CategoryActionMenu({
  menuId,
  openMenuId,
  onOpenChange,
  onEdit,
  onDelete,
  editLabel,
  deleteLabel,
}: {
  menuId: string;
  openMenuId: string | null;
  onOpenChange: (id: string | null) => void;
  onEdit: () => void;
  onDelete: () => void;
  editLabel: string;
  deleteLabel: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isOpen = openMenuId === menuId;

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onOpenChange(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, onOpenChange]);

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenChange(isOpen ? null : menuId);
        }}
        className="w-6 h-6 flex items-center justify-center transition-colors"
        aria-label="Actions"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className='dark:text-[#CBD5E1] text-[#475467]' xmlns="http://www.w3.org/2000/svg">
          <rect x="10.6504" y="4.12402" width="2.7" height="2.7" rx="1" stroke="currentColor" strokeWidth="2" />
          <rect x="10.6504" y="10.874" width="2.7" height="2.7" rx="1" stroke="currentColor" strokeWidth="2" />
          <rect x="10.6504" y="17.624" width="2.7" height="2.7" rx="1" stroke="currentColor" strokeWidth="2" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute right-0 top-full mt-1 z-50 min-w-[140px] sm:min-w-[200px] bg-white dark:bg-[#13354F] rounded-xl border border-[#F5F5F5] dark:border-[#2D4F68] shadow-[0px_8px_10px_0px_#00000012] dark:shadow-[0px_8px_10px_0px_#00000040] sm:py-1 sm:px-2 overflow-hidden">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenChange(null);
              onEdit();
            }}
            className="w-full flex items-center gap-2 p-3 text-[16px] leading-7 font-medium text-[#344054] dark:text-[#F8FAFC] transition-colors text-left border-b border-[#EAECF0] dark:border-zinc-700"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" className='dark:text-[#CBD5E1] text-[#141B34]' fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.8493 6.48826L11.4878 10.8498C10.6167 11.7209 10.1811 12.1564 9.65038 12.453C9.11963 12.7496 7.86637 13.048 6.66797 13.3333C6.9533 12.1349 7.2517 10.8817 7.54828 10.3509C7.84486 9.82018 8.28041 9.38463 9.15149 8.51354L13.513 4.15199L14.6812 2.98386C15.3263 2.33871 16.3723 2.33871 17.0174 2.98386C17.6626 3.629 17.6626 4.67498 17.0174 5.32013L15.8493 6.48826ZM13.513 4.15199L15.8493 6.48826" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M17.5 10C17.5 13.5355 17.5 15.3033 16.4016 16.4016C15.3033 17.5 13.5355 17.5 10 17.5C6.46447 17.5 4.6967 17.5 3.59835 16.4016C2.5 15.3033 2.5 13.5355 2.5 10C2.5 6.46447 2.5 4.6967 3.59835 3.59835C4.6967 2.5 6.46447 2.5 10 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            {editLabel}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenChange(null);
              onDelete();
            }}
            className="w-full flex items-center text-nowrap gap-2 p-3 text-[14px] sm:text-[16px] leading-5 sm:leading-7 font-medium text-[#D92D20] dark:text-[#F87171] transition-colors text-left"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className='dark:text-[#F87171] text-[#D92D20]' xmlns="http://www.w3.org/2000/svg">
              <path d="M16.25 4.5835L15.7336 12.9377C15.6016 15.0722 15.5356 16.1394 15.0006 16.9067C14.7361 17.2861 14.3955 17.6062 14.0006 17.8468C13.2018 18.3335 12.1325 18.3335 9.99395 18.3335C7.8526 18.3335 6.78192 18.3335 5.98254 17.8459C5.58733 17.6049 5.24666 17.2842 4.98224 16.9042C4.4474 16.1357 4.38288 15.0669 4.25384 12.9295L3.75 4.5835" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M2.5 4.58317H17.5M13.3798 4.58317L12.8109 3.40961C12.433 2.63005 12.244 2.24027 11.9181 1.99718C11.8458 1.94325 11.7692 1.89529 11.6892 1.85375C11.3283 1.6665 10.8951 1.6665 10.0288 1.6665C9.14069 1.6665 8.69665 1.6665 8.32974 1.8616C8.24842 1.90484 8.17082 1.95475 8.09774 2.01081C7.76803 2.26375 7.58386 2.6678 7.2155 3.47589L6.71077 4.58317" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M7.91602 13.75L7.91602 8.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M12.084 13.75L12.084 8.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            {deleteLabel}
          </button>
        </div>
      )}
    </div>
  );
}

function SortableCategoryRow({
  category,
  sectionCount,
  isEditing,
  editingName,
  onEditingNameChange,
  onStartEdit,
  onCommitEdit,
  onCancelEdit,
  onDelete,
  openMenuId,
  onOpenMenuChange,
  t,
}: RowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: category.id, disabled: isEditing });
  const style: React.CSSProperties = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 px-2 py-3 rounded-lg bg-[#F9FAFB] dark:bg-[#0E2B42] dark:border border-[#1E3A4E] dark:border-[#1E3A4E]"
    >
      <button
        {...attributes}
        {...listeners}
        type="button"
        className="touch-none cursor-grab active:cursor-grabbing flex-shrink-0 text-[#98A2B3] dark:text-zinc-500"
        aria-label={t('inventory.dragToReorder')}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className='text-[#475467] dark:text-[#CBD5E1]' xmlns="http://www.w3.org/2000/svg">
          <path d="M7 3.02441H7.00476M7 10.2244H7.00476M7 17.4244H7.00476M12.9952 3.02441H13M12.9952 10.2244H13M12.9952 17.4244H13" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      <div className="flex-1 min-w-0">
        {isEditing ? (
          <input
            type="text"
            autoFocus
            value={editingName}
            onChange={(e) => onEditingNameChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onCommitEdit(); if (e.key === 'Escape') onCancelEdit(); }}
            className="w-full px-3.5 py-3 rounded-lg border border-[#D0D5DD] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[16px] leading-5 font-semibold text-[#101828] dark:text-zinc-50 focus:outline-none"
          />
        ) : (
          <>
            <p className="text-[14px] sm:text-[16px] leading-5 font-semibold text-[#101828] dark:text-[#F8FAFC] truncate">{category.name}</p>
            <p className="text-[14px] leading-5 font-medium text-[#667085] dark:text-[#CBD5E1] mt-2">
              {sectionCount === 1
                ? t('inventory.categoryUsedInSections_one', { count: sectionCount })
                : t('inventory.categoryUsedInSections_other', { count: sectionCount })}
            </p>
          </>
        )}
      </div>

      {isEditing ? (
        <button
          type="button"
          onClick={onCommitEdit}
          className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg border border-[#0E8965] dark:border-[#32D583] text-[#0E8965] dark:text-[#32D583] hover:bg-[#EDFCF5] dark:hover:bg-green-900/20 transition-colors"
          aria-label={t('inventory.save')}
          title={t('inventory.save')}
        >
          <Check className="w-5 h-5" />
        </button>
      ) : (
        <CategoryActionMenu
          menuId={category.id}
          openMenuId={openMenuId}
          onOpenChange={onOpenMenuChange}
          onEdit={onStartEdit}
          onDelete={onDelete}
          editLabel={t('inventory.editCategory')}
          deleteLabel={t('inventory.deleteCategory')}
        />
      )}
    </li>
  );
}

export default function ManageInventoryCategoriesModal({ isOpen, onClose, restaurantId }: Props) {
  const { t } = useTranslation();
  const { categories, sectionProducts, createCategory, updateCategory, deleteCategory, reorderCategories } = useInventoryStore();
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setNewName('');
    setError(null);
    setBusy(false);
    setEditingId(null);
    setEditingName('');
    setOpenMenuId(null);
  }, [isOpen]);

  const sectionUsageByCategory = useMemo(() => {
    const counts = new Map<string, Set<string>>();
    for (const [sectionId, products] of Object.entries(sectionProducts)) {
      for (const p of products) {
        if (!p.inventoryCategoryId) continue;
        if (!counts.has(p.inventoryCategoryId)) counts.set(p.inventoryCategoryId, new Set());
        counts.get(p.inventoryCategoryId)!.add(sectionId);
      }
    }
    return counts;
  }, [sectionProducts]);

  const sorted = [...categories].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));

  const handleAdd = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      await createCategory({ restaurantId, name: trimmed });
      setNewName('');
    } catch (err) {
      setError(err instanceof CategoryNameExistsError ? t('inventory.categoryNameExists') : t('inventory.categorySaveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleRename = async (id: string) => {
    const trimmed = editingName.trim();
    if (!trimmed) { setEditingId(null); return; }
    setError(null);
    try {
      await updateCategory(id, { name: trimmed });
      setEditingId(null);
    } catch (err) {
      setError(err instanceof CategoryNameExistsError ? t('inventory.categoryNameExists') : t('inventory.categorySaveFailed'));
    }
  };

  const handleReorder = (reordered: InventoryCategory[]) => {
    reorderCategories(reordered.map((c, index) => ({ id: c.id, position: index })));
  };

  return (
    <InventorySlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={t('inventory.manageCategories')}
      description={t('inventory.manageCategoriesHint')}
      closeLabel={t('inventory.cancel') as string}
      footer={
        <>
          <button type="button" onClick={onClose} className={slideOverCancelClass}>
            {t('inventory.cancel')}
          </button>
          <button type="button" onClick={onClose} className={slideOverPrimaryClass}>
            {t('inventory.saveChanges')}
          </button>
        </>
      }
    >
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="flex gap-3 mb-5 sm:mb-6">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
          placeholder={t('inventory.categoryNamePlaceholder') as string}
          className="flex-1 px-4 h-11 rounded-xl border border-[#D0D5DD] dark:border-[#2D4F68] bg-white dark:bg-[#14344C] text-[14px] leading-5 font-semibold text-[#101828] dark:text-zinc-50 placeholder:text-[#667085] dark:placeholder:text-[#CBD5E1] focus:outline-none"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={busy || !newName.trim()}
          className="flex items-center gap-2 px-[11px] h-11 rounded-xl border border-[#0E8965] dark:border-[#32D583] text-[#0E8965] dark:text-[#32D583] font-semibold text-[14px] font-medium leading-5 hover:bg-[#EDFCF5] dark:hover:bg-green-900/20 disabled:opacity-50 transition-colors disabled:cursor-not-allowed"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.99935 2.6665L7.99935 13.3332M13.3327 7.99984L2.66602 7.99984" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {t('inventory.add')}
        </button>
      </div>

      {sorted.length === 0 ? (
        <p className="text-[14px] text-[#667085] dark:text-zinc-400 text-center py-8">{t('inventory.noCategories')}</p>
      ) : (
        <SortableList items={sorted} getId={(c) => c.id} onReorder={handleReorder}>
          <div className="border border-[#E8E8E9] dark:border-[#2D4F68] rounded-lg p-3 sm:p-4">
            <ul className="space-y-2 max-h-[calc(100vh-420px)] overflow-y-auto custom-scrollbar">
              {sorted.map((c) => (
                <SortableCategoryRow
                  key={c.id}
                  category={c}
                  sectionCount={sectionUsageByCategory.get(c.id)?.size ?? 0}
                  isEditing={editingId === c.id}
                  editingName={editingName}
                  onEditingNameChange={setEditingName}
                  onStartEdit={() => { setEditingId(c.id); setEditingName(c.name); setOpenMenuId(null); }}
                  onCommitEdit={() => handleRename(c.id)}
                  onCancelEdit={() => setEditingId(null)}
                  onDelete={() => deleteCategory(c.id, restaurantId)}
                  openMenuId={openMenuId}
                  onOpenMenuChange={setOpenMenuId}
                  t={t}
                />
              ))}
            </ul>
          </div>
        </SortableList>
      )}
    </InventorySlideOver>
  );
}
